import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchOrders, deleteAllOrders } from '../store/slices/ordersSlice';

const StatusBadge = ({ status }) => {
  const statusColors = {
    new: 'primary',
    in_queue: 'info',
    assigned: 'secondary',
    in_production: 'warning',
    quality_check: 'info',
    ready_for_shipping: 'success',
    shipped: 'success',
    on_hold: 'danger',
    cancelled: 'dark'
  };

  const statusLabels = {
    new: 'Nieuw',
    in_queue: 'In wachtrij',
    assigned: 'Toegewezen',
    in_production: 'In productie',
    quality_check: 'Quality check',
    ready_for_shipping: 'Gereed',
    shipped: 'Verzonden',
    on_hold: 'On hold',
    cancelled: 'Geannuleerd'
  };

  return (
    <span className={`badge bg-${statusColors[status] || 'secondary'}`}>
      {statusLabels[status] || status}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  if (priority >= 8) return <span className="badge bg-danger">Spoed</span>;
  if (priority >= 5) return <span className="badge bg-warning">Hoog</span>;
  if (priority >= 3) return <span className="badge bg-info">Normaal</span>;
  return <span className="badge bg-secondary">Laag</span>;
};

const Orders = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orders, pagination, loading } = useSelector((state) => state.orders);
  const { user } = useSelector((state) => state.auth);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState('');

  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    search: searchParams.get('search') || '',
    page: parseInt(searchParams.get('page')) || 1
  });

  const handleDeleteAll = async () => {
    const params = deleteStatus ? { status: deleteStatus } : {};
    await dispatch(deleteAllOrders(params));
    setShowDeleteModal(false);
    setDeleteStatus('');
    dispatch(fetchOrders(filters));
  };

  useEffect(() => {
    dispatch(fetchOrders(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    setSearchParams(new URLSearchParams(Object.entries(newFilters).filter(([_, v]) => v)));
  };

  const handlePageChange = (page) => {
    setFilters({ ...filters, page });
    setSearchParams(new URLSearchParams(Object.entries({ ...filters, page }).filter(([_, v]) => v)));
  };

  const statuses = [
    { value: '', label: 'Alle statussen' },
    { value: 'new', label: 'Nieuw' },
    { value: 'in_queue', label: 'In wachtrij' },
    { value: 'assigned', label: 'Toegewezen' },
    { value: 'in_production', label: 'In productie' },
    { value: 'ready_for_shipping', label: 'Gereed voor verzending' },
    { value: 'shipped', label: 'Verzonden' },
    { value: 'on_hold', label: 'On hold' }
  ];

  return (
    <div className="orders-page">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h1>Orders</h1>
          <p className="text-muted">Beheer en bekijk alle orders</p>
        </div>
        <div className="d-flex gap-2">
          {user?.role === 'admin' && (
            <button
              className="btn btn-outline-danger"
              onClick={() => setShowDeleteModal(true)}
            >
              <i className="bi bi-trash me-2"></i>
              Verwijder alle orders
            </button>
          )}
          <Link to="/settings" className="btn btn-outline-primary">
            <i className="bi bi-arrow-repeat me-2"></i>
            Sync met Picqer
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Zoeken</label>
              <input
                type="text"
                className="form-control"
                placeholder="Order nummer of klantnaam..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                {statuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Sorteren</label>
              <select
                className="form-select"
                value={filters.sort || '-createdAt'}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
              >
                <option value="-createdAt">Nieuwste eerst</option>
                <option value="createdAt">Oudste eerst</option>
                <option value="-priority">Hoogste prioriteit</option>
                <option value="orderNumber">Order nummer</option>
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-secondary w-100"
                onClick={() => {
                  setFilters({ status: '', search: '', page: 1 });
                  setSearchParams({});
                }}
              >
                Reset filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Laden...</span>
              </div>
            </div>
          ) : !orders || orders.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-3"></i>
              <p>Geen orders gevonden</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Klant</th>
                    <th>Items</th>
                    <th>Status</th>
                    <th>Prioriteit</th>
                    <th>Datum</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order._id}>
                      <td>
                        <Link to={`/orders/${order._id}`} className="fw-medium">
                          {order.orderNumber}
                        </Link>
                        {order.picqerReference && (
                          <small className="d-block text-muted">
                            Picqer: {order.picqerReference}
                          </small>
                        )}
                      </td>
                      <td>
                        <div>{order.customer?.name || '-'}</div>
                        <small className="text-muted">{order.customer?.email}</small>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark">
                          {order.items?.length || 0} items
                        </span>
                      </td>
                      <td>
                        <StatusBadge status={order.status} />
                      </td>
                      <td>
                        <PriorityBadge priority={order.priority} />
                      </td>
                      <td className="text-muted">
                        {new Date(order.createdAt).toLocaleDateString('nl-NL', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>
                      <td>
                        <Link
                          to={`/orders/${order._id}`}
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="bi bi-eye"></i>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="card-footer">
            <nav>
              <ul className="pagination justify-content-center mb-0">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Vorige
                  </button>
                </li>
                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                  const page = i + 1;
                  return (
                    <li
                      key={page}
                      className={`page-item ${pagination.page === page ? 'active' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Volgende
                  </button>
                </li>
              </ul>
            </nav>
            <div className="text-center text-muted small mt-2">
              Toont {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} van {pagination.total} orders
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Orders verwijderen</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-danger fw-bold">
                  Let op: Deze actie kan niet ongedaan worden gemaakt!
                </p>
                <div className="mb-3">
                  <label className="form-label">Welke orders verwijderen?</label>
                  <select
                    className="form-select"
                    value={deleteStatus}
                    onChange={(e) => setDeleteStatus(e.target.value)}
                  >
                    <option value="">Alle orders</option>
                    <option value="new">Alleen nieuwe orders</option>
                    <option value="cancelled">Alleen geannuleerde orders</option>
                    <option value="shipped">Alleen verzonden orders</option>
                    <option value="on_hold">Alleen on hold orders</option>
                  </select>
                </div>
                <p className="text-muted small">
                  {deleteStatus
                    ? `Alleen orders met status "${deleteStatus}" worden verwijderd.`
                    : 'Alle orders in het systeem worden verwijderd.'}
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteAll}
                  disabled={loading}
                >
                  {loading ? 'Bezig...' : 'Verwijderen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
